/**
 * Ce que le sas dit du morceau, et rien de ce qu'il en montre.
 *
 * Le sas est le dernier écran immobile avant le décompte : il ne juge rien, ne
 * compte rien, n'enseigne pas, et prépare les objets que le joueur va voir
 * bouger dix secondes plus tard. Sa mise en page est dans `sas-screen.tsx` ;
 * ici il n'y a que ce qui se teste, c'est-à-dire tout ce qui pourrait être faux :
 * la fusion des chiffrages, le groupement par qualité, l'ordre, les seuils des
 * phrases conditionnelles, et les deux positions de la vignette du niveau 2.
 *
 * Le partage est celui de `take.ts` et de `cursor.ts` : le dépôt n'a pas de
 * bibliothèque de test React, donc ce qui doit être couvert sort du composant.
 *
 * ## Dix accords, pas douze
 *
 * `Bm/D` et `Bm` sont le même accord pour le joueur : aux niveaux fabriqués la
 * main gauche tient la fondamentale, donc la basse écrite de l'analyse ne
 * décrit rien de ce qui se joue. L'inventaire fusionne donc sur le chiffrage
 * rogné, exactement comme le rail l'écrit, et pour la même raison.
 *
 * ## Quatre familles, et le groupement est la leçon
 *
 * Majeur, mineur, diminué, augmenté est précisément ce que le niveau 1 laisse
 * acquis. Dix symboles inconnus ne se retiennent pas, « quatre écarts, dix
 * endroits » se retient. Le groupement survit intact à `degreesTrusted` faux,
 * la qualité venant de la grille et non de la tonalité.
 *
 * Un accord sans tierce — `sus2`, `sus4`, quinte à vide — n'appartient à aucune
 * des quatre, et l'inventaire le laisse dehors plutôt que d'ouvrir une
 * cinquième famille : la phrase qui ouvre la liste promet « trois notes à
 * chaque fois », et une quinte à vide la démentirait sur la ligne suivante. Les
 * totaux se comptent donc sur ce qui reste, sans quoi la somme des groupes ne
 * ferait pas le nombre annoncé.
 */

import type { AnalysisChord } from "@/lib/music/analyzer-types";
import { keySignature } from "@/lib/music/key";
import { chordToneName, letterIndex, pitchClassToName, spellOnLetter } from "@/lib/music/theory-helpers";
import { tonicPitchClass, type Tonality } from "@/lib/music/tonality";
import { chordPosition } from "./position";
import { withoutWrittenBass } from "./cursor";

/**
 * Les niveaux qui ont un sas, par indice canonique dans `LADDER_SHAPE`.
 *
 * Les deux premiers, et pour l'instant eux seuls. Ce sont ceux où l'écran de
 * jeu ne dit nulle part ce qu'il attend : le mécanisme central — le morceau
 * écrit qui sonne ralenti pendant qu'on pose l'accord par-dessus — n'est écrit
 * sur aucune surface, et le bandeau n'a rien à en dire non plus. Il présente
 * bien l'observation de la fenêtre, mais le niveau 1 n'en publie aucune, faute
 * d'un niveau avant lui, et celles du niveau 2 nomment un renversement sans
 * dire ce qu'on attend de la main. Les sept autres gardent leurs cartes
 * existante, la couture, qui répond à une autre question.
 *
 * L'indice canonique et non le rang affiché, pour la même raison que
 * `isSeamRung` : `RungMap` renumérote sans trou, et un morceau à sept niveaux
 * ouvrirait son sas au mauvais endroit.
 */
export function isSasRung(index: number): boolean {
  return index === 0 || index === 1;
}

/** Les quatre écarts que le niveau 1 laisse acquis. */
export type ChordFamily = "minor" | "major" | "augmented" | "diminished";

/**
 * Ce qu'une famille est, et ce qu'elle empile.
 *
 * Les demi-tons servent la vignette du niveau 2, qui ne dessine que des
 * triades : c'est la même table qui décide du groupe et de la forme, donc les
 * deux ne peuvent pas se contredire. Elle ne double pas `chordIntervals`, qui
 * répond d'un vocabulaire plus large — septièmes comprises — et vit du côté des
 * composants, là où `lib/` n'a pas le droit d'aller.
 */
const FAMILIES: Record<ChordFamily, { readonly label: string; readonly triad: readonly [number, number, number] }> = {
  minor: { label: "MINEUR", triad: [0, 3, 7] },
  major: { label: "MAJEUR", triad: [0, 4, 7] },
  augmented: { label: "AUGMENTÉ", triad: [0, 4, 8] },
  diminished: { label: "DIMINUÉ", triad: [0, 3, 6] },
};

/** L'ordre canonique, qui ne départage que les égalités de poids. */
const FAMILY_ORDER: readonly ChordFamily[] = ["minor", "major", "augmented", "diminished"];

/**
 * La famille d'une qualité de l'analyseur, ou `null` quand elle n'a pas de
 * tierce.
 *
 * Les septièmes retombent sur leur triade, qui est ce que le niveau 1 fait
 * jouer : un `dominant7` est un accord majeur sous les mains, et le ranger
 * ailleurs mentirait sur ce qu'on va prendre.
 */
export function chordFamily(quality: string): ChordFamily | null {
  switch (quality) {
    case "minor":
    case "m":
    case "m7":
    case "min7":
    case "m6":
    case "min6":
    case "minmaj7":
    case "mM7":
      return "minor";

    case "major":
    case "maj7":
    case "7":
    case "dominant7":
    case "6":
    case "maj6":
      return "major";

    case "augmented":
    case "aug7":
      return "augmented";

    case "diminished":
    case "dim7":
      return "diminished";

    default:
      return null;
  }
}

/**
 * Une note d'un accord, telle que la ligne de l'inventaire l'écrit.
 *
 * L'orthographe est anglaise et la hauteur voyage à côté : l'écran met la
 * première dans la notation du joueur, et la seconde est ce qui décide si la
 * note est dans la gamme. Deux questions, deux champs — un nom déjà traduit ne
 * se compare plus à rien.
 */
export type SasTone = {
  readonly name: string;
  readonly pitchClass: number;
};

/** Un accord du morceau, une fois toutes ses fenêtres réunies. */
export type SasChord = {
  /** Le chiffrage rogné de sa basse écrite, glyphe pour glyphe celui du fil. */
  readonly symbol: string;
  readonly root: string;
  readonly quality: string;
  readonly rootPitchClass: number;
  /** Le chiffre romain, ou `""` quand l'analyseur n'en donne pas. */
  readonly romanNumeral: string;
  readonly family: ChordFamily;
  /**
   * Les trois notes qu'on va prendre, et pas celles que le chiffrage annonce.
   *
   * La triade de la famille, donc `D7` s'écrit *ré fa♯ la* : le niveau 1 pose
   * une triade sous les mains, c'est déjà la raison pour laquelle les septièmes
   * retombent sur leur famille, et la phrase qui ouvre l'écran promet trois
   * notes à chaque fois. Une quatrième colonne sur la seule ligne d'un accord
   * de septième démentirait la phrase et annoncerait une note que personne ne
   * joue.
   *
   * Épelées sur le degré qu'elles occupent dans l'accord et non sur leur
   * hauteur : la tierce de sol mineur est un si bémol, jamais un la dièse.
   */
  readonly notes: readonly SasTone[];
  /** Nombre de fenêtres du morceau. */
  readonly count: number;
};

export type SasGroup = {
  readonly family: ChordFamily;
  /** `MINEUR`, `MAJEUR`, `AUGMENTÉ`, `DIMINUÉ`. */
  readonly label: string;
  readonly chords: readonly SasChord[];
  /** Les fenêtres du groupe : c'est ce qui l'ordonne. */
  readonly windows: number;
};

/** Ce que la ligne de repli ramasse. */
export type SasFold = {
  readonly chords: number;
  /** Tous repliés parce qu'ils ne reviennent pas, et pas parce que la liste est longue. */
  readonly onlyOnce: boolean;
};

export type SasInventory = {
  /** Accords distincts, ligne de repli comprise. */
  readonly chords: number;
  /** Fenêtres d'accord du morceau, ligne de repli comprise. */
  readonly windows: number;
  readonly groups: readonly SasGroup[];
  /**
   * Les lignes du tableau, à plat et par fréquence décroissante.
   *
   * Le même contenu que `groups`, dans l'autre ordre, et les deux servent : le
   * tableau se lit par fréquence, ce que le morceau ramène le plus en premier,
   * et les familles restent comptées parce que la phrase qui ouvre la colonne
   * de droite les annonce. Reconstruire l'un depuis l'autre dans l'écran
   * remettrait un tri dans un composant.
   */
  readonly rows: readonly SasChord[];
  readonly fold: SasFold | null;
  /**
   * Les deux plus fréquents, quand ils couvrent au moins un tiers des fenêtres.
   *
   * `null` sinon, et la seconde phrase de l'amorce ne sort pas : elle ne doit
   * jamais annoncer une domination qui n'existe pas.
   */
  readonly dominant: readonly [SasChord, SasChord] | null;
  /**
   * Les accords de la liste qui partent de la même note, par fondamentale.
   *
   * Dans l'ordre chromatique des fondamentales, et dans l'ordre de la liste à
   * l'intérieur d'une fondamentale. Vide quand aucune n'est partagée.
   */
  readonly sharedRoots: readonly (readonly SasChord[])[];
};

/**
 * Au-delà, la liste cesse d'être un inventaire et devient un tableau.
 *
 * Compté en lignes d'accord, titres de groupe exclus : le nombre de groupes
 * dépend du morceau, et un seuil qui bougerait avec lui replierait deux
 * morceaux voisins à des endroits différents.
 */
const MAX_ROWS = 14;

/** Ce qu'on garde quand on replie pour longueur. */
const KEPT_ROWS = 12;

/**
 * En dessous, replier coûte une ligne pour en gagner une.
 */
const MIN_FOLD = 3;

/** L'inventaire des accords du morceau, tel que le bloc central le pose. */
export function chordInventory(chords: readonly AnalysisChord[]): SasInventory {
  const merged = merge(chords);
  const windows = merged.reduce((sum, entry) => sum + entry.count, 0);

  // Ordre global : par fréquence décroissante, égalité départagée par la
  // première apparition dans le morceau. `merged` est déjà dans l'ordre des
  // premières apparitions, et le tri est stable.
  const byWeight = [...merged].sort((a, b) => b.count - a.count);

  const folded = toFold(byWeight);
  const rows = byWeight.filter((entry) => !folded.includes(entry));
  const groups = toGroups(rows);

  return {
    chords: merged.length,
    windows,
    groups,
    rows,
    fold:
      folded.length === 0
        ? null
        : { chords: folded.length, onlyOnce: folded.every((entry) => entry.count === 1) },
    dominant: toDominant(byWeight, windows),
    sharedRoots: toSharedRoots(groups),
  };
}

/**
 * Les fenêtres réunies par chiffrage rogné, dans l'ordre des premières
 * apparitions.
 *
 * Le chiffre romain est celui de la première fenêtre qui en porte un : un
 * accord peut en manquer sur une fenêtre sans en manquer partout, et une
 * cellule vide alors que le morceau chiffre ailleurs dirait qu'on ne sait pas.
 */
function merge(chords: readonly AnalysisChord[]): SasChord[] {
  const bySymbol = new Map<string, SasChord>();

  for (const chord of chords) {
    const family = chordFamily(chord.quality);

    if (family === null) {
      continue;
    }

    const symbol = withoutWrittenBass(chord.symbol);
    const seen = bySymbol.get(symbol);

    if (seen === undefined) {
      bySymbol.set(symbol, {
        symbol,
        root: chord.root,
        quality: chord.quality,
        rootPitchClass: chord.rootPitchClass,
        romanNumeral: chord.romanNumeral ?? "",
        family,
        notes: triadTones(chord.root, chord.rootPitchClass, family),
        count: 1,
      });

      continue;
    }

    bySymbol.set(symbol, {
      ...seen,
      romanNumeral: seen.romanNumeral === "" ? (chord.romanNumeral ?? "") : seen.romanNumeral,
      count: seen.count + 1,
    });
  }

  return [...bySymbol.values()];
}

/**
 * Les trois notes de la triade, épelées.
 *
 * `chordToneName` monte la lettre du nombre de degrés de l'intervalle, ce qui
 * est la seule façon d'écrire *si bémol* et non *la dièse* pour la tierce de
 * sol mineur. Il rend `null` au-delà du double accidentel, où il n'y a plus de
 * nom raisonnable ; l'orthographe simple prend alors le relais plutôt que de
 * laisser une colonne vide.
 */
function triadTones(
  root: string,
  rootPitchClass: number,
  family: ChordFamily,
): readonly SasTone[] {
  return FAMILIES[family].triad.map((interval) => {
    const pitchClass = (((rootPitchClass + interval) % 12) + 12) % 12;

    return {
      name: chordToneName(root, rootPitchClass, pitchClass) ?? pitchClassToName(pitchClass, root),
      pitchClass,
    };
  });
}

/**
 * Ce que la ligne de repli ramasse, dans l'ordre : d'abord ce qui ne revient
 * pas, ensuite ce que la longueur oblige.
 *
 * Les deux passes visent la même ligne, et la seconde ne s'ouvre que si la
 * première n'a pas suffi.
 */
function toFold(byWeight: readonly SasChord[]): readonly SasChord[] {
  const once = byWeight.filter((entry) => entry.count === 1);
  const folded = once.length >= MIN_FOLD ? once : [];
  const rest = byWeight.filter((entry) => !folded.includes(entry));

  if (rest.length <= MAX_ROWS) {
    return folded;
  }

  return [...folded, ...rest.slice(KEPT_ROWS)];
}

/**
 * Les groupes, par poids, et vides retirés.
 *
 * La première ligne de l'écran est donc l'accord le plus rencontré : le poids
 * du groupe le porte, et sa fréquence le porte à l'intérieur.
 */
function toGroups(shown: readonly SasChord[]): SasGroup[] {
  const groups: SasGroup[] = [];

  for (const family of FAMILY_ORDER) {
    const chords = shown.filter((entry) => entry.family === family);

    if (chords.length === 0) {
      continue;
    }

    groups.push({
      family,
      label: FAMILIES[family].label,
      chords,
      windows: chords.reduce((sum, entry) => sum + entry.count, 0),
    });
  }

  // Tri stable, donc l'ordre canonique départage les poids égaux.
  return groups.sort((a, b) => b.windows - a.windows);
}

/** Les deux plus fréquents, s'ils couvrent au moins un tiers des fenêtres. */
function toDominant(
  byWeight: readonly SasChord[],
  windows: number,
): readonly [SasChord, SasChord] | null {
  const [first, second] = byWeight;

  if (first === undefined || second === undefined) {
    return null;
  }

  return (first.count + second.count) * 3 >= windows ? [first, second] : null;
}

/**
 * Les fondamentales qu'au moins deux accords de la liste partagent.
 *
 * Sur la liste montrée et non sur le morceau : la phrase dit « ici », et elle
 * désigne des lignes que le joueur a sous les yeux.
 */
function toSharedRoots(groups: readonly SasGroup[]): readonly (readonly SasChord[])[] {
  const flat = groups.flatMap((group) => group.chords);
  const byRoot = new Map<number, SasChord[]>();

  for (const entry of flat) {
    const family = byRoot.get(entry.rootPitchClass) ?? [];

    family.push(entry);
    byRoot.set(entry.rootPitchClass, family);
  }

  return [...byRoot.entries()]
    .filter(([, family]) => family.length >= 2)
    .sort(([a], [b]) => a - b)
    .map(([, family]) => family);
}

// ── La gamme du morceau ────────────────────────────────────────

/**
 * Un degré de la gamme, et ce que le morceau bâtit dessus.
 *
 * Le chiffre romain vient des accords et non de la théorie : la colonne dit
 * « ce morceau-ci pose *iv* sur son quatrième degré », pas « le quatrième degré
 * d'une gamme mineure porte un accord mineur ». Une table de degrés dirait la
 * seconde, qui est vraie partout et n'apprend rien de celui qu'on ouvre.
 */
export type SasDegree = {
  /** L'orthographe anglaise, que l'écran met dans la notation du joueur. */
  readonly name: string;
  readonly pitchClass: number;
  /** Son rang, de 1 à 7. */
  readonly degree: number;
  /** Les chiffrages romains des accords du tableau bâtis sur ce degré. */
  readonly romanNumerals: readonly string[];
};

/**
 * Une note que le morceau joue et que sa gamme n'a pas.
 *
 * Elle n'est pas une erreur et ne se corrige pas : c'est la sensible d'un
 * mineur harmonique, une dominante secondaire, un emprunt. La nommer par le
 * degré dont elle prend la place — *♯7* et non *fa♯ tout court* — est ce qui la
 * rattache à la gamme au lieu de l'en exclure.
 */
export type SasBorrowed = {
  readonly name: string;
  readonly pitchClass: number;
  /** `♯7`, `♭2` : le degré altéré dont elle prend la place. */
  readonly degree: string;
  readonly romanNumerals: readonly string[];
};

/** L'armure, et les notes qu'elle porte. */
export type SasSignature = {
  readonly count: number;
  readonly accidental: "sharp" | "flat" | "natural";
  /** Les altérations dans l'ordre où elles s'écrivent sur la portée. */
  readonly notes: readonly string[];
};

/**
 * La gamme du morceau, ses degrés, son armure et ce qu'il emprunte à côté.
 *
 * Le cadre dans lequel le tableau se lit. Sans elle, une colonne de notes est
 * une liste de lettres ; avec elle, on voit que *fa♯* revient sur trois lignes
 * et n'est dans la gamme d'aucune — c'est-à-dire qu'on voit la seule chose que
 * ce morceau fait et que la gamme ne prévoit pas.
 */
export type SasScale = {
  readonly tonality: Tonality;
  /** Les sept degrés, de la tonique à la sensible. */
  readonly degrees: readonly SasDegree[];
  /** Les notes hors gamme du tableau, par degré croissant. */
  readonly borrowed: readonly SasBorrowed[];
  readonly signature: SasSignature | null;
  /** Les sept hauteurs, pour dire d'une note du tableau si elle en est. */
  readonly pitchClasses: ReadonlySet<number>;
};

/** Les intervalles des deux modes, en demi-tons depuis la tonique. */
const MAJOR_STEPS: readonly number[] = [0, 2, 4, 5, 7, 9, 11];

/**
 * Le mineur **naturel**, et c'est une décision.
 *
 * Les trois échelles du mineur se croisent dans un même morceau, et prendre
 * l'harmonique ici ferait rentrer la sensible dans la gamme : *fa♯* cesserait
 * d'être une note empruntée dans un sol mineur, alors que c'est précisément ce
 * que l'écran a à montrer. Le naturel est celui de l'armure, donc le seul dont
 * la colonne des notes et la ligne « deux bémols à la clé » disent la même
 * chose.
 */
const MINOR_STEPS: readonly number[] = [0, 2, 3, 5, 7, 8, 10];

/** L'ordre où les altérations s'écrivent à la clé. */
const FLAT_ORDER: readonly string[] = ["Bb", "Eb", "Ab", "Db", "Gb", "Cb", "Fb"];
const SHARP_ORDER: readonly string[] = ["F#", "C#", "G#", "D#", "A#", "E#", "B#"];

/**
 * La gamme du morceau, ou `null` quand la tonique ne s'écrit pas.
 *
 * `null` plutôt qu'une gamme approximative : l'écran retire alors la fiche, la
 * colonne des degrés et l'encre des notes empruntées d'un seul geste, ce qui
 * est le comportement qu'il a déjà quand l'analyseur ne répond pas des degrés.
 * Une gamme mal épelée ferait pire que rien, en désignant comme empruntées des
 * notes qui n'ont d'étrange que leur orthographe.
 */
export function sasScale(tonality: Tonality, inventory: SasInventory): SasScale | null {
  const base = letterIndex(tonality.tonic);

  if (base < 0) {
    return null;
  }

  const tonicPc = tonicPitchClass(tonality.tonic);
  const steps = tonality.mode === "major" ? MAJOR_STEPS : MINOR_STEPS;
  const degrees: SasDegree[] = [];

  for (let at = 0; at < steps.length; at += 1) {
    const pitchClass = (tonicPc + steps[at]!) % 12;
    const name = spellOnLetter(base + at, pitchClass);

    if (name === null) {
      return null;
    }

    degrees.push({
      name,
      pitchClass,
      degree: at + 1,
      romanNumerals: numeralsOn(inventory.rows, pitchClass),
    });
  }

  const pitchClasses = new Set(degrees.map((entry) => entry.pitchClass));

  return {
    tonality,
    degrees,
    borrowed: toBorrowed(inventory.rows, degrees, pitchClasses),
    signature: toSignature(tonality),
    pitchClasses,
  };
}

/** L'armure de cette tonalité, notes comprises. */
function toSignature(tonality: Tonality): SasSignature | null {
  const armure = keySignature(tonality.tonic, tonality.mode);

  if (armure === null) {
    return null;
  }

  const order = armure.accidental === "flat" ? FLAT_ORDER : SHARP_ORDER;

  return {
    count: armure.count,
    accidental: armure.accidental,
    notes: armure.accidental === "natural" ? [] : order.slice(0, armure.count),
  };
}

/**
 * Les notes du tableau que la gamme n'a pas, par degré croissant.
 *
 * Sur les lignes montrées et non sur le morceau entier, comme la ligne des
 * fondamentales partagées : la fiche explique ce qu'on a sous les yeux, et une
 * note empruntée par un accord replié n'apparaîtrait nulle part ailleurs.
 *
 * La première orthographe rencontrée gagne. Deux lignes peuvent écrire la même
 * hauteur autrement — *fa♯* et *sol♭* — et l'ordre du tableau est celui des
 * fréquences, donc c'est l'accord le plus fréquent qui nomme la note.
 */
function toBorrowed(
  rows: readonly SasChord[],
  degrees: readonly SasDegree[],
  pitchClasses: ReadonlySet<number>,
): readonly SasBorrowed[] {
  const seen = new Map<number, string>();

  for (const row of rows) {
    for (const tone of row.notes) {
      if (pitchClasses.has(tone.pitchClass) || seen.has(tone.pitchClass)) {
        continue;
      }

      seen.set(tone.pitchClass, tone.name);
    }
  }

  return [...seen.entries()]
    .map(([pitchClass, name]) => ({
      pitchClass,
      name,
      degree: alteredDegree(name, pitchClass, degrees),
      romanNumerals: numeralsOn(rows, pitchClass),
    }))
    .sort((a, b) => rank(a.degree) - rank(b.degree));
}

/**
 * Le degré altéré dont une note prend la place, ou `""` quand on ne sait pas
 * le dire.
 *
 * Par la **lettre** : les sept lettres sont dans la gamme une fois chacune, donc
 * *mi* désigne le sixième degré d'un sol mineur sans ambiguïté, et l'accidentel
 * est l'écart qui reste. C'est aussi ce qui donne *♯6* plutôt que *♭7*, alors
 * que les deux sonnent pareil : la note s'écrit *mi* et non *fa♭*.
 */
function alteredDegree(
  name: string,
  pitchClass: number,
  degrees: readonly SasDegree[],
): string {
  const letter = letterIndex(name);
  const at = degrees.find((entry) => letterIndex(entry.name) === letter);

  if (at === undefined) {
    return "";
  }

  let shift = (((pitchClass - at.pitchClass) % 12) + 12) % 12;

  if (shift > 6) {
    shift -= 12;
  }

  if (shift === 0 || Math.abs(shift) > 2) {
    return "";
  }

  return (shift > 0 ? "♯" : "♭").repeat(Math.abs(shift)) + at.degree;
}

/** Le rang d'un degré altéré dans la liste, les illisibles à la fin. */
function rank(degree: string): number {
  const digits = degree.replace(/\D/g, "");

  return digits === "" ? 99 : Number(digits);
}

/**
 * Les chiffrages romains des accords du tableau bâtis sur cette hauteur.
 *
 * Dans l'ordre du tableau, donc du plus fréquent au moins fréquent, et
 * dédoublonnés sur le chiffrage **nu** : `V` et `V7` sont le même degré, et une
 * ligne de gamme qui écrirait les deux dirait qu'il y en a deux.
 */
function numeralsOn(rows: readonly SasChord[], pitchClass: number): readonly string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const row of rows) {
    if (row.rootPitchClass !== pitchClass) {
      continue;
    }

    const bare = bareNumeral(row.romanNumeral);

    if (bare === "" || seen.has(bare)) {
      continue;
    }

    seen.add(bare);
    out.push(bare);
  }

  return out;
}

/**
 * Le chiffrage sans ses chiffres arabes ni sa basse.
 *
 * `V7` et `V65` sont `V` : ce que la ligne de gamme désigne est un degré, et
 * les chiffres disent ce qu'on empile dessus, ce que la colonne des notes
 * montre déjà. L'altération devant et le signe derrière restent, eux : `♯vii°`
 * n'est pas `vii`.
 */
const NUMERAL = /^([#b♯♭]*)([ivxIVX]+)([°+ø]?)/;

function bareNumeral(numeral: string): string {
  const found = NUMERAL.exec(numeral.trim());

  return found === null ? "" : `${found[1]}${found[2]}${found[3]}`;
}

// ── Les phrases ────────────────────────────────────────────────

/**
 * Un entier en toutes lettres.
 *
 * L'écran écrit « Dix accords, trente-neuf fois en tout » et pas « 10 accords,
 * 39 fois » : c'est une phrase, et une phrase se lit. Les chiffres reviennent
 * dans la colonne des fréquences, qui est un tableau et non un texte.
 *
 * Au-delà de mille on rend le nombre : aucun morceau du catalogue n'a mille
 * fenêtres d'accord, et une règle d'accord de « quatre-vingts » dans un nombre
 * composé coûterait plus cher à relire qu'elle ne rapporte.
 */
export function spellNumber(value: number): string {
  if (!Number.isFinite(value) || value < 0 || value >= 1000 || !Number.isInteger(value)) {
    return String(value);
  }

  if (value < 20) {
    return UNITS[value];
  }

  if (value < 100) {
    return spellTens(value);
  }

  const hundreds = Math.floor(value / 100);
  const rest = value % 100;
  const head = hundreds === 1 ? "cent" : `${UNITS[hundreds]} cent`;

  if (rest === 0) {
    return hundreds === 1 ? head : `${head}s`;
  }

  return `${head} ${spellNumber(rest)}`;
}

const UNITS = [
  "zéro", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf",
  "dix", "onze", "douze", "treize", "quatorze", "quinze", "seize",
  "dix-sept", "dix-huit", "dix-neuf",
];

const TENS: Record<number, string> = {
  2: "vingt",
  3: "trente",
  4: "quarante",
  5: "cinquante",
  6: "soixante",
  8: "quatre-vingt",
};

function spellTens(value: number): string {
  // Soixante-dix et quatre-vingt-dix comptent par vingtaines : leurs unités
  // vont jusqu'à dix-neuf, ce qui donne « soixante et onze » et non « soixante
  // et un dix ».
  if (value >= 70 && value < 80) {
    return join("soixante", value - 60);
  }

  if (value >= 90) {
    return `quatre-vingt-${UNITS[value - 80]}`;
  }

  const ten = Math.floor(value / 10);
  const unit = value % 10;

  if (unit === 0) {
    return ten === 8 ? "quatre-vingts" : TENS[ten];
  }

  // Quatre-vingt-un ne prend pas le « et » : c'est la seule dizaine construite
  // par multiplication et non par addition.
  return ten === 8 ? `quatre-vingt-${UNITS[unit]}` : join(TENS[ten], unit);
}

function join(ten: string, unit: number): string {
  return unit === 1 || unit === 11 ? `${ten} et ${UNITS[unit]}` : `${ten}-${UNITS[unit]}`;
}

/** La même chose, en tête de phrase. */
export function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** Bloc 3, première phrase : ce que ce niveau rencontre dans ce morceau. */
export function inventoryLead(inventory: SasInventory): string {
  return `${capitalize(spellNumber(inventory.chords))} accords, ${spellNumber(inventory.windows)} fois en tout.`;
}

/**
 * Bloc 3, seconde phrase, avec les deux noms déjà épelés.
 *
 * Les noms arrivent en toutes lettres et non en symboles : c'est une phrase, et
 * `Bm` demande de connaître la convention des lettres anglaises, ce que le
 * produit retire ailleurs.
 */
export function dominantLead(first: string, second: string, windows: number): string {
  return `${first} et ${second} en font ${spellNumber(windows)} à eux deux.`;
}

/**
 * Bloc 4, phrase d'entrée.
 *
 * Le nombre de familles se compte, il ne s'écrit pas : « quatre » est vrai du
 * *Lac des cygnes* et faux d'un morceau qui n'en croise que deux.
 */
export function spreadLead(families: number): string {
  return (
    "Trois notes à chaque fois. Ce qui change d'un accord à l'autre, c'est l'écart entre elles, " +
    `et il n'y en a que ${spellNumber(families)} ici.`
  );
}

/** Bloc 4, la légende qui explique le chiffre romain et porte la tonalité. */
export function degreeLegend(tonality: string): string {
  return `Le chiffre romain dit le degré : la place de l'accord dans ${tonality}, la tonalité du morceau.`;
}

/**
 * Bloc 4, la ligne des fondamentales partagées, avec les noms déjà épelés.
 *
 * `null` quand aucune n'est partagée : sans exemple, la phrase n'apprendrait
 * rien qu'elle ne postule.
 */
export function sharedRootsLine(families: readonly (readonly string[])[]): string | null {
  if (families.length === 0) {
    return null;
  }

  const named = families.map(list).join(", ");

  return `Deux accords peuvent partir de la même note : ici ${named}. C'est ce qu'on empile dessus qui change.`;
}

function list(names: readonly string[]): string {
  if (names.length <= 1) {
    return names[0] ?? "";
  }

  return `${names.slice(0, -1).join(", ")} et ${names[names.length - 1]}`;
}

/**
 * La ligne de repli.
 *
 * Deux formulations et pas une, parce que les deux passes ne ramassent pas la
 * même chose : « croisés une seule fois » est un fait quand la liste replie ses
 * accords rares, et un mensonge quand elle replie aussi sa queue par longueur.
 */
export function foldLine(fold: SasFold): string {
  return fold.onlyOnce
    ? `et ${spellNumber(fold.chords)} accords croisés une seule fois`
    : `et ${spellNumber(fold.chords)} autres accords`;
}

/**
 * Bloc 5, dernière phrase : de combien d'accords le niveau est fait.
 *
 * Des accords, et non des unités : le niveau se traverse d'un trait, et
 * l'unité n'est plus un objet que le joueur rencontre. Ce qu'il enchaîne,
 * c'est une fenêtre d'accord après l'autre, et c'est ce que le sas compte.
 */
export function chordsLine(chords: number): string {
  return chords === 1
    ? "Un seul accord."
    : `${capitalize(spellNumber(chords))} accords, l'un après l'autre.`;
}

/**
 * La fréquence dans la colonne de l'inventaire, ou `null` sous deux fenêtres.
 *
 * Une seule occurrence ne se dit pas : « une fois » occupe une cellule pour
 * annoncer qu'il n'y a rien à annoncer, et la cellule vide marque d'elle-même
 * les accords rares.
 *
 * Le seuil vivait dans `recurrenceLine`, qui écrivait la même chose en entier
 * pour le bandeau du niveau — « douze fois dans le morceau ». Cette phrase-là a
 * été retirée : dans la colonne étroite qui lui restait elle se brisait en cinq
 * lignes d'un mot, et elle ne servait pas. Le sas est donc le dernier endroit
 * qui compte les fenêtres d'un accord, et il garde le seuil avec le compte.
 *
 * Il le dit court. Le bandeau le portait seul dans une colonne où il devait se
 * suffire ; ici c'est la troisième cellule d'une liste dont l'écran entier dit
 * qu'elle parle de ce morceau, et « dans le morceau » y serait répété quatorze
 * fois pour rien, au prix de la largeur qui permet à la liste de couler en deux
 * colonnes.
 */
export function recurrenceCell(count: number): string | null {
  return count < 2 ? null : `${count} fois`;
}

/** La ligne repliée de la version courte, celle qui rouvre l'inventaire. */
export function inventoryToggle(chords: number): string {
  return `Les ${spellNumber(chords)} accords de ce morceau`;
}

/**
 * L'armure, en toutes lettres.
 *
 * Sous le nom de la tonalité, où elle répond à la question que ce nom pose à
 * quelqu'un qui n'a jamais lu de partition : *sol mineur* ne dit rien, *deux
 * bémols à la clé, si♭ et mi♭* dit lesquelles des touches noires sont dans le
 * morceau.
 */
export function signatureLine(signature: SasSignature): string {
  if (signature.accidental === "natural" || signature.count === 0) {
    return "pas d'altération à la clé";
  }

  const word = signature.accidental === "flat" ? "bémol" : "dièse";

  return `${spellNumber(signature.count)} ${word}${signature.count > 1 ? "s" : ""} à la clé`;
}

/** Ce que la bande de la fiche montre, selon qu'il y a un emprunt ou non. */
export function scaleCaption(borrowed: number): string {
  return borrowed === 0 ? "Gamme et degrés" : "Gamme, degrés et notes empruntées";
}

/**
 * La légende de la couleur, sous le tableau.
 *
 * Elle ne sort que s'il y a quelque chose en violet à expliquer : une légende
 * qui décrit une encre absente envoie chercher ce qui n'y est pas.
 */
export function borrowedLegend(borrowed: number): string | null {
  if (borrowed === 0) {
    return null;
  }

  return borrowed === 1 ? "en violet : la note hors gamme" : "en violet : les notes hors gamme";
}

/**
 * Le repère du bandeau : où l'on est dans l'échelle, et ce qu'il y a à faire.
 *
 * En chiffres et non en toutes lettres, contrairement aux phrases : c'est une
 * étiquette de position, lue d'un coup d'œil et jamais lue à voix haute.
 *
 * Le rang est celui de l'échelle **de ce morceau**, comme la carte des niveaux
 * le renumérote : un morceau qui n'a rien à apprendre au niveau canonique 2
 * ouvre son troisième au rang trois, et pas au rang quatre.
 */
export function rungRail(rank: number, chords: number): string {
  return `Niveau ${rank} · ${chords} ${chords === 1 ? "accord" : "accords"}`;
}

// ── L'entrée : le morceau, et les deux parcours ────────────────


// ── Le niveau 2 : les trois positions ──────────────────────────

/**
 * Une fenêtre du niveau, réduite à ce que le sas en montre.
 *
 * Les notes **du niveau** et non celles du morceau, ce qui est la seule raison
 * pour laquelle cet objet vient de l'appelant plutôt que d'ici : `HarmonyContext`
 * porte les accords et les notes d'origine, tandis que ce qui va tomber sous les
 * mains est dans le morceau chargé, que `HarmonyLevel` découpe déjà pour son
 * rail. Le redécouper ici en ferait une seconde réponse à « qu'est-ce qu'on
 * joue », qui divergerait le jour où l'une des deux se tromperait de niveau.
 *
 * La main droite est frappée **et** tenue : c'est la pile entière qui donne la
 * position, exactement comme le rail la lit.
 */
export type SasWindow = {
  /** Le chiffrage rogné de sa basse écrite, comme partout ailleurs. */
  readonly symbol: string;
  /** L'orthographe de la fondamentale, qui épelle les notes de la fenêtre. */
  readonly root: string;
  readonly rootPitchClass: number;
  /**
   * Les classes de hauteur que l'accord énonce, ou `null` s'il est illisible.
   *
   * La fenêtre liste ce que les mains jouent, et une gamme y met bien plus que
   * l'accord : sans cette borne, un `la` par-dessus un mi♭ se faisait épeler
   * par l'accord et sortait `si♭♭`. Voir `windowToneName`.
   */
  readonly chordPitchClasses: ReadonlySet<number> | null;
  /** Ce que l'analyseur a écrit, touche par touche. Voir `windowToneName`. */
  readonly spelling: ReadonlyMap<number, string> | null;
  readonly left: readonly number[];
  readonly right: readonly number[];
};

/** Une des trois positions, et ce que ce niveau en fait dans ce morceau. */
export type SasPosition = {
  /** `0` fondamentale, `1` premier renversement, `2` deuxième. */
  readonly inversion: number;
  /** L'ordre des degrés dans la pile, la note du bas d'abord. */
  readonly degrees: readonly string[];
  /** Fenêtres du niveau qui prennent cette position. */
  readonly count: number;
};

/**
 * Les degrés d'une triade, dans l'ordre où la fondamentale les empile.
 *
 * Une position est une **rotation** de ces trois-là, et c'est tout : la table
 * n'a donc que trois cases et les trois lignes de la fiche s'en déduisent. Les
 * écrire à la main en aurait fait neuf, dont six recopiées.
 */
const TRIAD_DEGREES: readonly string[] = ["1", "3", "5"];

/**
 * Combien de fois ce niveau prend chacune des trois positions.
 *
 * Comptées sur la pile de la main droite par `chordPosition`, qui est la même
 * fonction que celle du rail de rythme : la fiche et le dessin ne peuvent donc
 * pas se contredire. Et sur les fenêtres du niveau plutôt que sur les
 * observations de l'analyseur, qui sont vides partout où un niveau ne change
 * rien et obligeraient à compter l'état fondamental par soustraction.
 *
 * Les positions absentes gardent leur ligne. Les trois se lisent ensemble et le
 * zéro est une information : sur un morceau qui n'en croise que deux, une fiche
 * à deux lignes laisserait croire qu'il n'en existe que deux.
 */
export function positionSpread(windows: readonly SasWindow[]): readonly SasPosition[] {
  const counts = [0, 0, 0];

  for (const window of windows) {
    const at = chordPosition(window.rootPitchClass, window.right);

    if (at >= 0 && at < counts.length) {
      counts[at] += 1;
    }
  }

  return counts.map((count, inversion) => ({
    inversion,
    degrees: [...TRIAD_DEGREES.slice(inversion), ...TRIAD_DEGREES.slice(0, inversion)],
    count,
  }));
}

/**
 * Les premières fenêtres du niveau, celles que le dessin montre.
 *
 * Le début et non la boucle du morceau : l'analyseur publie bien une boucle
 * harmonique, mais son propre commentaire dit qu'elle est absente de la plupart
 * des morceaux, et un dessin qui s'annoncerait comme la boucle en montrerait un
 * début une fois sur deux. Ce qu'il y a à voir ne demande pas la boucle : deux
 * changements d'accord suffisent à faire voir que la main droite tient ses
 * notes pendant que l'accord change.
 */
export function firstWindows(
  windows: readonly SasWindow[],
  count: number,
): readonly SasWindow[] {
  return windows.slice(0, count);
}

/**
 * Bloc 3, première phrase du niveau 2 : ce qui ne change pas.
 *
 * Les deux mêmes nombres qu'au niveau 1, et c'est le propos : le morceau n'a
 * pas bougé, seule la main a changé d'avis sur l'ordre de ses notes. Un écran
 * qui rouvrirait son inventaire ici ferait croire à un nouvel inventaire.
 */
export function sameChordsLead(inventory: SasInventory): string {
  return (
    `Les mêmes ${spellNumber(inventory.chords)} accords, ` +
    `les mêmes ${spellNumber(inventory.windows)} passages.`
  );
}
